import { LanguageLoader } from './decorators/sh-use-translation.decorator';

export interface ScopeInfo {
	/**
	 * The label scope name prefixing labels in this label scope
	 */
	name: string;

	/**
	 * Callback for loading labels for a specific language code
	 */
	loader: LanguageLoader;

	/**
	 * All language codes loaded (or attempted but failed to load).
	 */
	loadedLanguages: Set<string>;
}

/**
 * Service for collecting translation scopes required by code using decorators.
 *
 * This class works in concert with
 * - shUseTranslation decorator, which is used for marking classes as requiring a given translation scope
 *
 * Since this service is used by class decorators it can't depend on much other code - those decorators run when code is being
 * loaded (not when the classes are instantiated) and so can't rely on anything not loaded along with them.
 */
export class TranslationScopeService {
	/**
	 * The label scopes required by code
	 */
	public readonly scopes = new Map<string, ScopeInfo>();

	/**
	 * A callback invoked when code has required a label scope.
	 */
	public scopeAddedCallback: ((scope: ScopeInfo) => void) | undefined = undefined;

	/**
	 * A callback invoked when new code has loaded which requires labels to be loaded
	 * for added label scopes.
	 */
	public loadMissingLabelsCallback: () => Promise<void> = () => Promise.resolve();

	private scopesAddedSinceLastImportEvent = false;

	/**
	 * Request that a given label scope be loaded at the next round of label loading
	 *
	 * @param scope Scope name to use for labels loaded. I.e. the prefix scope name to put before a label name to access it.
	 * @param loader A function taking the filename without path of a translation file to be loaded, and returns the labels
	 *                contained in it. Akin to "(lang) => import(`../../${lang}`)".
	 */
	public requireScope(scope: string, loader: LanguageLoader): void {
		if (!this.scopes.has(scope)) {
			const scopeInfo = {
				name: scope,
				loader: loader,
				loadedLanguages: new Set<string>()
			};
			this.scopes.set(scope, scopeInfo);
			this.scopesAddedSinceLastImportEvent = true;

			this.scopeAddedCallback?.(scopeInfo);
		}
	}

	/**
	 * Inform the translation scope subsystem that new code modules have been loaded and that
	 * labels must be loaded for any required labels scopes not already loaded.
	 *
	 * @returns Promise which will resolve once any required label loading has finished.
	 */
	public async updateAfterImportingCode(): Promise<void> {
		if (this.scopesAddedSinceLastImportEvent) {
			this.scopesAddedSinceLastImportEvent = false;

			await this.loadMissingLabelsCallback();
		}
	}
}

/**
 * Global instance of TranslationScopeService for decorators to use.
 * Can't rely on dependency injection since class decorators run too early for that.
 */
export const translationScopeService = new TranslationScopeService();
